<template>
  <div class="review-container">
    <WaiverReviewFilters @filter="handleFilter" />
    <TheLoading v-if="loadingWaivers" />
    <TheFieldset
      v-else
      legend="View Completed Waivers"
    >
      <DataTable
        v-if="waivers.length"
        :value="waivers"
        :sortField="sorting.sortField"
        :sortOrder="sorting.sortOrder"
        scrollable
        scrollHeight="540px"
        tableStyle="min-width: 50rem"
        @update:sortField="updateSortField"
        @update:sortOrder="updateSortOrder"
      >
        <Column
          field="createdOn"
          header="Completed Date"
          sortable
        >
          <template #body="slotProps">
            <p>{{ formatDate2(slotProps.data.createdOn) }}</p>
          </template>
        </Column>
        <Column
          field="firstName"
          header="First Name"
          sortable
        />
        <Column
          field="lastName"
          header="Last Name"
          sortable
        />
        <Column
          field="email"
          header="Email"
          sortable
        />
        <Column
          field="hasViolation"
          header="A/V?"
          sortable
        >
          <template #body="slotProps">
            <Checkbox
              v-model="slotProps.data.hasViolation"
              binary disabled
            />
          </template>
        </Column>
      </DataTable>
      <p v-else class="no-results">No results found!</p>
      <button
        type="button"
        class="black-btn"
        :disabled="exportingCSV"
        @click="exportCSV"
      >
        <span v-if="exportingCSV" class="loader">Exporting <span /></span>
        <span v-else>Export Data</span>
      </button>
    </TheFieldset>
  </div>
</template>

<script setup>
import TheLoading from '@/components/TheLoading.vue';
import { onMounted, ref } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Checkbox from 'primevue/checkbox';
import { useToast } from 'vue-toastification';

import api from '@/api';
// import TheButton from '@/components/global/TheButton.vue';
import TheFieldset from '@/components/global/TheFieldset.vue';
import { formatDate2 } from '@/utils/format';
import WaiverReviewFilters from './WaiverReviewFilters.vue';

const toast = useToast();

const props = defineProps({
  token: { type: String, required: true },
});

const waivers = ref([]);

const lastFilters = ref({});
const loadingWaivers = ref(false);
const exportingCSV = ref(false);
const sorting = ref({ sortField: 'createdOn', sortOrder: 1 });

const updateSortField = (event) => {
  sorting.value.sortField = event;
};

const updateSortOrder = (event) => {
  sorting.value.sortOrder = event;
};

const fetchWaivers = async (filters) => {
  loadingWaivers.value = true;
  try {
    const { data } = await api.waiver.getWaivers(props.token, filters);
    waivers.value = data;
  } catch ({ message }) {
    toast.error(message || 'An unexpected error occurred');
  } finally {
    loadingWaivers.value = false;
  }
};

const handleFilter = (filters) => {
  const parsedFilters = { ...filters };
  if (filters.from) {
    parsedFilters.from = new Date(filters.from).toISOString();
  }
  if (filters.to) {
    parsedFilters.to = new Date(filters.to).toISOString();
  }
  lastFilters.value = parsedFilters;
  fetchWaivers(parsedFilters);
};

const exportCSV = async () => {
  exportingCSV.value = true;
  try {
    const { data } = await api.waiver.getWaiverCSV(props.token, lastFilters.value);
    const blob = new Blob([data], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'Storytelling_Competition_Waiver_Export.csv';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch ({ message }) {
    toast.error(message || 'An unexpected error occurred');
  } finally {
    exportingCSV.value = false;
  }
};

onMounted(() => {
  if (props.token) fetchWaivers({});
});
</script>

<style lang="scss" scoped>
.review-container {
  padding: 0 20px 80px;
  display: flex;
  flex-direction: column;
  gap: 40px;
  font-family: 'Avenir';

  .no-results {
    padding: 40px 20px;
    font-size: 20px;
  }

  .black-btn {
    margin-top: 40px;
    min-width: 200px;
  }

  .loader {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;

    span {
      border: 5px solid #fff;
      border-top: 5px solid #4291d1;
      border-radius: 50%;
      width: 25px;
      height: 25px;
      animation: spin 2s linear infinite;
    }
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
}
</style>
